/**
 * Tenant Learning Engine
 * ML-powered learning system with complete tenant isolation
 * Each company learns independently with their own vocabulary and patterns
 */

import { collection, doc, getDoc, setDoc, updateDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/client';
import { getCompanyId } from '../tenantContext';
import { indexedDBService } from '../offline/indexedDB';

export interface CommandPattern {
  pattern: string;
  action: string;
  count: number;
  confidence: number;
  examples: string[];
  lastUsed: Date;
  createdBy: string;
}

export interface CorrectionHistory {
  id?: string;
  original: string;
  corrected: string;
  timestamp: Date;
  userId: string;
  applied: boolean;
  userFeedback?: 'correct' | 'incorrect';
}

export interface CommandLog {
  id?: string;
  command: string;
  action: string;
  success: boolean;
  confidence: number;
  method: string;
  timestamp: Date;
  userId: string;
  executionTime: number;
}

export interface TenantLearningData {
  companyId: string;
  commandPatterns: Record<string, CommandPattern>;
  customVocabulary: Record<string, string>; // "unit 5" → "Well Alpha-5"
  wellNicknames: Record<string, string>; // "the big one" → "Well Alpha-12"
  successfulCommands: CommandLog[];
  failedCommands: CommandLog[];
  userCorrections: CorrectionHistory[];
  lastTrainingDate: Date;
  modelVersion: string;
  stats: {
    totalCommands: number;
    successRate: number;
    avgConfidence: number;
    topPatterns: string[];
  };
}

export interface PredictionResult {
  action: string;
  confidence: number;
  method: 'tenant-learned' | 'pattern-match' | 'semantic' | 'fallback';
  params?: Record<string, any>;
  alternativeActions?: Array<{ action: string; confidence: number }>;
}

class TenantLearningEngine {
  private static instance: TenantLearningEngine;
  private cache: Map<string, TenantLearningData> = new Map();
  private cacheExpiry: Map<string, number> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private constructor() {
    this.initializeIndexedDB();
  }

  static getInstance(): TenantLearningEngine {
    if (!TenantLearningEngine.instance) {
      TenantLearningEngine.instance = new TenantLearningEngine();
    }
    return TenantLearningEngine.instance;
  }

  /**
   * Initialize IndexedDB for offline learning
   * Note: IndexedDB stores are created during database upgrade in indexedDBService.initialize()
   */
  private async initializeIndexedDB(): Promise<void> {
    try {
      // IndexedDB stores are automatically created during initialize()
      // No explicit ensureStore needed - stores are defined in indexedDB.ts upgrade handler
    } catch (error) {
      console.error('Failed to initialize learning IndexedDB:', error);
    }
  }

  /**
   * Get tenant learning data (cached)
   */
  async getTenantData(companyId: string): Promise<TenantLearningData> {
    // Check cache
    const cached = this.cache.get(companyId);
    const expiry = this.cacheExpiry.get(companyId);
    
    if (cached && expiry && Date.now() < expiry) {
      return cached;
    }

    // Try IndexedDB first (offline support)
    try {
      const offline = await indexedDBService.get('learning_patterns', companyId);
      if (offline) {
        this.cache.set(companyId, offline);
        this.cacheExpiry.set(companyId, Date.now() + this.CACHE_TTL);
        return offline;
      }
    } catch (error) {
    }

    // Load from Firestore
    try {
      const docRef = doc(db, `companies/${companyId}/xassist_learning`, 'patterns');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as TenantLearningData;
        data.companyId = companyId;
        
        // Cache it
        this.cache.set(companyId, data);
        this.cacheExpiry.set(companyId, Date.now() + this.CACHE_TTL);
        
        // Store offline
        await indexedDBService.put('learning_patterns', data);
        
        return data;
      }
    } catch (error) {
      console.error('Failed to load tenant learning data:', error);
    }

    // Return default structure
    return this.createDefaultData(companyId);
  }

  /**
   * Create default learning data structure
   */
  private createDefaultData(companyId: string): TenantLearningData {
    return {
      companyId,
      commandPatterns: {},
      customVocabulary: {},
      wellNicknames: {},
      successfulCommands: [],
      failedCommands: [],
      userCorrections: [],
      lastTrainingDate: new Date(),
      modelVersion: '1.0.0',
      stats: {
        totalCommands: 0,
        successRate: 0,
        avgConfidence: 0,
        topPatterns: []
      }
    };
  }

  /**
   * Record successful command execution
   */
  async recordSuccess(
    command: string,
    action: string,
    params: any,
    companyId: string,
    userId: string,
    confidence: number,
    method: string,
    executionTime: number
  ): Promise<void> {
    const data = await this.getTenantData(companyId);

    // 1. Extract and store pattern
    const pattern = this.extractPattern(command);
    if (pattern) {
      if (!data.commandPatterns[pattern]) {
        data.commandPatterns[pattern] = {
          pattern,
          action,
          count: 1,
          confidence: confidence || 0.5,
          examples: [command],
          lastUsed: new Date(),
          createdBy: userId
        };
      } else {
        const existing = data.commandPatterns[pattern];
        existing.count++;
        existing.confidence = Math.min(0.95, existing.confidence + 0.05);
        existing.lastUsed = new Date();
        
        // Add example if unique
        if (!existing.examples.includes(command) && existing.examples.length < 10) {
          existing.examples.push(command);
        }
      }
    }

    // 2. Learn vocabulary from params
    if (params.wellId && params.wellName) {
      await this.learnWellReference(command, params.wellName, companyId);
    }

    // 3. Log successful command
    const log: CommandLog = {
      command,
      action,
      success: true,
      confidence,
      method,
      timestamp: new Date(),
      userId,
      executionTime
    };
    data.successfulCommands.push(log);

    // Keep only last 100 successful commands
    if (data.successfulCommands.length > 100) {
      data.successfulCommands = data.successfulCommands.slice(-100);
    }

    // 4. Update stats
    data.stats.totalCommands++;
    data.stats.successRate = 
      data.successfulCommands.length / 
      (data.successfulCommands.length + data.failedCommands.length);
    
    const avgConf = data.successfulCommands.reduce((sum, c) => sum + c.confidence, 0) / 
                    data.successfulCommands.length;
    data.stats.avgConfidence = avgConf;

    // 5. Update top patterns
    const patternCounts = Object.entries(data.commandPatterns)
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, 10)
      .map(([pattern]) => pattern);
    data.stats.topPatterns = patternCounts;

    // 6. Save
    await this.saveTenantData(companyId, data);
  }

  /**
   * Record failed command
   */
  async recordFailure(
    command: string,
    action: string,
    companyId: string,
    userId: string,
    confidence: number,
    method: string
  ): Promise<void> {
    const data = await this.getTenantData(companyId);

    const log: CommandLog = {
      command,
      action: action || 'unknown',
      success: false,
      confidence,
      method,
      timestamp: new Date(),
      userId,
      executionTime: 0
    };

    data.failedCommands.push(log);

    // Keep only last 50 failed commands
    if (data.failedCommands.length > 50) {
      data.failedCommands = data.failedCommands.slice(-50);
    }

    // Update success rate
    data.stats.successRate = 
      data.successfulCommands.length / 
      (data.successfulCommands.length + data.failedCommands.length);

    await this.saveTenantData(companyId, data);
  }

  /**
   * Record user correction
   */
  async recordCorrection(
    originalCommand: string,
    correctedIntent: string,
    companyId: string,
    userId: string
  ): Promise<void> {
    const data = await this.getTenantData(companyId);

    const correction: CorrectionHistory = {
      original: originalCommand,
      corrected: correctedIntent,
      timestamp: new Date(),
      userId,
      applied: false
    };

    data.userCorrections.push(correction);

    // Apply correction to vocabulary if it's a well reference
    const wellMatch = correctedIntent.match(/well\s+([a-z0-9\-]+)/i);
    if (wellMatch) {
      const extractedName = wellMatch[0];
      data.customVocabulary[originalCommand.toLowerCase()] = extractedName;
    }

    await this.saveTenantData(companyId, data);
  }

  /**
   * Get prediction for a command
   */
  async getPrediction(
    command: string,
    companyId: string
  ): Promise<PredictionResult | null> {
    const data = await this.getTenantData(companyId);
    const normalizedCommand = this.normalizeCommand(command, data);

    // 1. Check exact pattern match
    const pattern = this.extractPattern(normalizedCommand);
    if (pattern && data.commandPatterns[pattern]) {
      const match = data.commandPatterns[pattern];
      return {
        action: match.action,
        confidence: match.confidence,
        method: 'tenant-learned'
      };
    }

    // 2. Check vocabulary substitutions
    const withVocab = this.applyVocabulary(normalizedCommand, data);
    if (withVocab !== normalizedCommand) {
      const vocabPattern = this.extractPattern(withVocab);
      if (vocabPattern && data.commandPatterns[vocabPattern]) {
        const match = data.commandPatterns[vocabPattern];
        return {
          action: match.action,
          confidence: match.confidence * 0.9, // Slightly lower confidence for vocab match
          method: 'pattern-match'
        };
      }
    }

    // 3. Semantic similarity with learned examples
    const semanticMatch = await this.findSemanticMatch(normalizedCommand, data);
    if (semanticMatch) {
      return semanticMatch;
    }

    return null;
  }

  /**
   * Extract pattern from command (generalized)
   */
  private extractPattern(command: string): string {
    let pattern = command.toLowerCase().trim();
    
    // Replace specific well names with wildcard
    pattern = pattern.replace(/well\s+[a-z0-9\-]+/gi, 'well_*');
    
    // Replace numbers with wildcard
    pattern = pattern.replace(/\d+(\.\d+)?/g, '#');
    
    // Replace dates
    pattern = pattern.replace(/\b(today|tomorrow|yesterday|next\s+\w+)\b/gi, 'DATE');
    
    return pattern;
  }

  /**
   * Normalize command with vocabulary
   */
  private normalizeCommand(command: string, data: TenantLearningData): string {
    let normalized = command.toLowerCase();
    
    // Apply custom vocabulary
    for (const [key, value] of Object.entries(data.customVocabulary)) {
      normalized = normalized.replace(new RegExp(key, 'gi'), value);
    }
    
    // Apply well nicknames
    for (const [nickname, wellName] of Object.entries(data.wellNicknames)) {
      normalized = normalized.replace(new RegExp(`\\b${nickname}\\b`, 'gi'), wellName);
    }
    
    return normalized;
  }

  /**
   * Apply vocabulary substitutions
   */
  private applyVocabulary(command: string, data: TenantLearningData): string {
    return this.normalizeCommand(command, data);
  }

  /**
   * Learn well reference from command
   */
  private async learnWellReference(
    command: string,
    wellName: string,
    companyId: string
  ): Promise<void> {
    // Extract potential nicknames or shorthand
    const lowerCommand = command.toLowerCase();
    const lowerWell = wellName.toLowerCase();
    
    // If command doesn't contain full well name, learn the reference
    if (!lowerCommand.includes(lowerWell)) {
      const data = await this.getTenantData(companyId);
      
      // Extract the reference used
      const wellRef = lowerCommand.match(/(?:well|unit)\s+([a-z0-9\-]+)/i)?.[1];
      if (wellRef && wellRef !== wellName) {
        data.wellNicknames[wellRef] = wellName;
        await this.saveTenantData(companyId, data);
      }
    }
  }

  /**
   * Find semantic match using cosine similarity
   */
  private async findSemanticMatch(
    command: string,
    data: TenantLearningData
  ): Promise<PredictionResult | null> {
    const patterns = Object.values(data.commandPatterns);
    if (patterns.length === 0) return null;

    let bestMatch: { pattern: CommandPattern; score: number } | null = null;

    for (const pattern of patterns) {
      for (const example of pattern.examples) {
        const similarity = this.calculateSimilarity(command, example);
        if (!bestMatch || similarity > bestMatch.score) {
          bestMatch = { pattern, score: similarity };
        }
      }
    }

    if (bestMatch && bestMatch.score > 0.7) {
      return {
        action: bestMatch.pattern.action,
        confidence: bestMatch.score * bestMatch.pattern.confidence,
        method: 'semantic'
      };
    }

    return null;
  }

  /**
   * Calculate string similarity (simple Jaccard)
   */
  private calculateSimilarity(str1: string, str2: string): number {
    const words1 = new Set(str1.toLowerCase().split(/\s+/));
    const words2 = new Set(str2.toLowerCase().split(/\s+/));
    
    const intersection = new Set([...words1].filter(w => words2.has(w)));
    const union = new Set([...words1, ...words2]);
    
    return intersection.size / union.size;
  }

  /**
   * Save tenant data to Firestore and IndexedDB
   */
  private async saveTenantData(companyId: string, data: TenantLearningData): Promise<void> {
    data.lastTrainingDate = new Date();
    
    // Update cache
    this.cache.set(companyId, data);
    this.cacheExpiry.set(companyId, Date.now() + this.CACHE_TTL);

    // Save to IndexedDB (offline)
    try {
      await indexedDBService.put('learning_patterns', data);
    } catch (error) {
    }

    // Save to Firestore
    try {
      const docRef = doc(db, `companies/${companyId}/xassist_learning`, 'patterns');
      await setDoc(docRef, {
        ...data,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Failed to save tenant learning data:', error);
      throw error;
    }
  }

  /**
   * Get learning statistics for tenant
   */
  async getStatistics(companyId: string): Promise<TenantLearningData['stats']> {
    const data = await this.getTenantData(companyId);
    return data.stats;
  }

  /**
   * Export learning data for analysis
   */
  async exportLearningData(companyId: string): Promise<TenantLearningData> {
    return this.getTenantData(companyId);
  }

  /**
   * Clear tenant learning data (admin function)
   */
  async clearTenantData(companyId: string): Promise<void> {
    const defaultData = this.createDefaultData(companyId);
    await this.saveTenantData(companyId, defaultData);
    this.cache.delete(companyId);
    this.cacheExpiry.delete(companyId);
  }
}

// Export singleton
export const tenantLearningEngine = TenantLearningEngine.getInstance();
